import React, { useEffect, useState } from 'react';
import { Box, Button, Card, CardContent, Typography, Stack, Link, IconButton, TextField, Grid } from '@mui/material';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { ThemeStyle } from '../../module/ThemeManager';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { IPastWork, IWorkProfile } from '../../types/global/helper';
import { useDispatch } from 'react-redux';
import { addCredit, deleteCredit, updateCredit } from '../../redux/talentProfile/action';
import { IAddCredit } from '../../redux/talentProfile/types';
import { useThemeContext } from '../../hook/useThemeManager';
import { CompanySearchSelect } from '../CompanySearchSelect';
import { ICompany } from '../../redux/company/types';
import { CompanyAvatar } from '../Avatar/CompanyAvatar';

interface CreditsTimelineProps {
    themeStyle: ThemeStyle;
    canEdit?: boolean;
    workProfile: IWorkProfile;
}

const SortableCredit = ({
    credit,
    onEdit,
    onDelete,
    index,
    isEditing,
    onEditSubmit,
    onEditCancel,
    canEdit,
}: {
    credit: IPastWork;
    onEdit: (credit: IPastWork) => void;
    onDelete: (id: string) => void;
    themeStyle: ThemeStyle;
    index: number;
    isEditing: boolean;
    onEditSubmit: (credit: IPastWork) => void;
    onEditCancel: () => void;
    canEdit?: boolean;
}) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: credit._id });
    const { themeStyle } = useThemeContext();

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <Box ref={setNodeRef} style={style} position="relative">
            <FiberManualRecordIcon
                color="primary"
                sx={{
                    position: 'absolute',
                    left: -30,
                    top: 8,
                    fontSize: 18,
                    zIndex: 1,
                    bgcolor: theme[themeStyle].secondaryBg,
                    borderRadius: '50%',
                    color: theme[themeStyle].highlight,
                }}
            />
            {isEditing ? (
                <CreditForm credit={credit} onSubmit={onEditSubmit} onCancel={onEditCancel} themeStyle={themeStyle} />
            ) : (
                <Card
                    variant="outlined"
                    sx={{
                        width: '100%',
                        backgroundColor: theme[themeStyle].secondaryBg,
                        borderColor: theme[themeStyle].gray_text,
                        '&:hover': {
                            borderColor: theme[themeStyle].highlight,
                        },
                    }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                            {canEdit && (
                                <IconButton
                                    {...attributes}
                                    {...listeners}
                                    size="small"
                                    sx={{
                                        color: theme[themeStyle].tag_white,
                                        mt: 1,
                                        '&:hover': {
                                            color: theme[themeStyle].highlight,
                                        },
                                    }}>
                                    <DragIndicatorIcon />
                                </IconButton>
                            )}
                            <Box sx={{ flex: 1 }}>
                                <Typography
                                    variant="h6"
                                    fontWeight="bold"
                                    sx={{
                                        color: theme[themeStyle].primary_white,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                    }}>
                                    {credit.title}
                                </Typography>
                                <Stack direction={'row'} gap={1} alignItems={'center'}>
                                    {credit.company && typeof credit.company !== 'string' && (
                                        <CompanyAvatar company={credit.company} enableDefaultNavigation size="small" />
                                    )}
                                    <Typography
                                        color="text.secondary"
                                        sx={{
                                            color: theme[themeStyle].light_gray_text,
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        }}>
                                        {typeof credit.company === 'string'
                                            ? credit.company
                                            : credit.company?.company_name}{' '}
                                        • {credit.type}
                                    </Typography>
                                </Stack>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontStyle: 'italic',
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    }}>
                                    Role: {credit.role?.join(' • ')} • Year: {credit.year}
                                </Typography>
                                <Link
                                    href={credit.link}
                                    target="_blank"
                                    rel="noopener"
                                    sx={{
                                        color: theme[themeStyle].main_blue,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        fontStyle: 'italic',
                                        fontSize: 14,
                                        '&:hover': {
                                            color: theme[themeStyle].highlight,
                                        },
                                    }}>
                                    View Project
                                </Link>
                            </Box>
                            {canEdit && (
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <IconButton
                                        size="small"
                                        onClick={() => onEdit(credit)}
                                        sx={{
                                            color: theme[themeStyle].tag_white,
                                            '&:hover': {
                                                color: theme[themeStyle].highlight,
                                            },
                                        }}>
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={() => onDelete(credit._id)}
                                        sx={{
                                            color: theme[themeStyle].tag_white,
                                            '&:hover': {
                                                color: theme[themeStyle].highlight,
                                            },
                                        }}>
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                            )}
                        </Box>
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

const CreditForm = ({
    credit,
    onSubmit,
    onCancel,
    themeStyle,
}: {
    credit?: IPastWork;
    onSubmit: (credit: IPastWork) => void;
    onCancel: () => void;
    themeStyle: ThemeStyle;
}) => {
    const [formData, setFormData] = useState<IPastWork>(
        credit || {
            _id: Date.now().toString(),
            title: '',
            year: '',
            role: [],
            link: '',
            type: '',
            description: '',
        },
    );

    const projectTypes = [
        'short film',
        'music video',
        'feature film',
        'photo shoot',
        'student film',
        'TV series',
        'commercial',
        'web series',
        'private work',
        'documentary',
        'theater production',
        'live performance',
        'virtual production',
        'animation',
        'reality show',
        'podcast',
        'design work',
        'animation',
        'color grading',
        'others',
    ];

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const payload: any = {
            ...formData,
        };
        if (formData.company) {
            payload.company = formData.company._id;
        }
        onSubmit(payload);
    };

    return (
        <Card
            variant="outlined"
            sx={{
                width: '100%',
                mb: 3,
                maxWidth: '100%',
                backgroundColor: theme[themeStyle].secondaryBg,
                borderColor: theme[themeStyle].gray_text,
            }}>
            <CardContent sx={{ maxWidth: '100%' }}>
                <form onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Project Name"
                                value={formData.title}
                                onChange={e => setFormData({ ...formData, title: e.target.value })}
                                required
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: theme[themeStyle].gray_text,
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        '& fieldset': {
                                            borderColor: theme[themeStyle].placeholderGray50,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: theme[themeStyle].placeholderGray50,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: theme[themeStyle].tag_white,
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <CompanySearchSelect
                                value={formData.company}
                                onChange={value => {
                                    // For work place, we only want single selection
                                    const company = Array.isArray(value)
                                        ? (value[0] as ICompany) || null
                                        : (value as ICompany);
                                    setFormData({ ...formData, company: company });
                                }}
                                placeholder="Search select or add a business or company"
                                multiple={false}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Year"
                                value={formData.year}
                                onChange={e => setFormData({ ...formData, year: e.target.value })}
                                required
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: theme[themeStyle].gray_text,
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        '& fieldset': {
                                            borderColor: theme[themeStyle].placeholderGray50,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: theme[themeStyle].placeholderGray50,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: theme[themeStyle].tag_white,
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                select
                                label="Project Type"
                                value={formData.type}
                                onChange={e => setFormData({ ...formData, type: e.target.value })}
                                required
                                SelectProps={{
                                    native: true,
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: theme[themeStyle].gray_text,
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        '& fieldset': {
                                            borderColor: theme[themeStyle].placeholderGray50,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: theme[themeStyle].placeholderGray50,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: theme[themeStyle].tag_white,
                                    },
                                    '& .MuiSelect-select': {
                                        color: theme[themeStyle].tag_white,
                                    },
                                    '& .MuiSelect-icon': {
                                        color: theme[themeStyle].tag_white,
                                    },
                                }}>
                                <option value="" disabled>
                                    Select a type
                                </option>
                                {projectTypes.map(type => (
                                    <option key={type} value={type}>
                                        {type.charAt(0).toUpperCase() + type.slice(1)}
                                    </option>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Role"
                                value={formData.role}
                                onChange={e => setFormData({ ...formData, role: [e.target.value] })}
                                required
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: theme[themeStyle].gray_text,
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        '& fieldset': {
                                            borderColor: theme[themeStyle].placeholderGray50,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: theme[themeStyle].placeholderGray50,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: theme[themeStyle].tag_white,
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Description"
                                value={formData.description}
                                onChange={e => setFormData({ ...formData, description: e.target.value })}
                                multiline
                                rows={1}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: theme[themeStyle].gray_text,
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        '& fieldset': {
                                            borderColor: theme[themeStyle].placeholderGray50,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: theme[themeStyle].placeholderGray50,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: theme[themeStyle].tag_white,
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Project Link"
                                value={formData.link}
                                onChange={e => setFormData({ ...formData, link: e.target.value })}
                                required
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: theme[themeStyle].gray_text,
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        '& fieldset': {
                                            borderColor: theme[themeStyle].placeholderGray50,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: theme[themeStyle].placeholderGray50,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme[themeStyle].highlight,
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: theme[themeStyle].tag_white,
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                <Button
                                    variant="outlined"
                                    onClick={onCancel}
                                    sx={{
                                        borderColor: theme[themeStyle].placeholderGray,
                                        color: theme[themeStyle].primary_white,
                                        '&:hover': {
                                            borderColor: theme[themeStyle].placeholderGray,
                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                        },
                                    }}>
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    type="submit"
                                    sx={{
                                        backgroundColor: theme[themeStyle].highlight,
                                        color: theme[themeStyle].black,
                                        '&:hover': {
                                            backgroundColor: theme[themeStyle].primary_orange,
                                            opacity: 0.9,
                                        },
                                    }}>
                                    {credit ? 'Update' : 'Add'} Credit
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </form>
            </CardContent>
        </Card>
    );
};

const CreditsTimeline: React.FC<CreditsTimelineProps> = ({ themeStyle, canEdit = false, workProfile }) => {
    const [credits, setCredits] = useState<IPastWork[]>(workProfile.credits ?? []);
    const [showForm, setShowForm] = useState(false);
    const [editingCredit, setEditingCredit] = useState<IPastWork | undefined>();
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const dispatch = useDispatch();

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            setCredits(items => {
                const oldIndex = items.findIndex(item => item._id === active.id);
                const newIndex = items.findIndex(item => item._id === over.id);
                return arrayMove(items, oldIndex, newIndex);
            });
        }
    };

    const handleAddCredit = (credit: IPastWork) => {
        setCredits([...credits, credit]);
        setShowForm(false);
        dispatch(
            addCredit({
                ...(credit as unknown as IAddCredit),
                profileId: workProfile._id,
            }),
        );
    };

    const handleEditCredit = (credit: IPastWork) => {
        const index = credits.findIndex(c => c._id === credit._id);
        setEditingCredit(credit);
        setEditingIndex(index);
    };

    const handleUpdateCredit = (credit: IPastWork) => {
        setCredits(credits.map(c => (c._id === credit._id ? credit : c)));
        setEditingCredit(undefined);
        setEditingIndex(null);
        dispatch(updateCredit({ documentId: credit._id, payload: credit }));
    };

    const handleDeleteCredit = (id: string) => {
        setCredits(credits.filter(credit => credit._id !== id));
        const credit = credits.find(credit => credit._id !== id);
        dispatch(
            deleteCredit({
                documentId: id,
                payload: credit,
            }),
        );
    };

    useEffect(() => {
        if (workProfile.credits) setCredits(workProfile.credits);
    }, [workProfile.credits]);

    return (
        <Box sx={{ mx: 'auto', mt: 4, px: 2, mb: 4 }}>
            {canEdit && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => {
                            setEditingCredit(undefined);
                            setShowForm(true);
                        }}
                        sx={{
                            backgroundColor: theme[themeStyle].highlight,
                            color: theme[themeStyle].black,
                            '&:hover': {
                                backgroundColor: theme[themeStyle].primary_orange,
                                opacity: 0.9,
                            },
                        }}>
                        Add New Credit
                    </Button>
                </Box>
            )}

            {showForm && !editingCredit && canEdit && (
                <CreditForm onSubmit={handleAddCredit} onCancel={() => setShowForm(false)} themeStyle={themeStyle} />
            )}

            <Stack spacing={4} position="relative" pl={4}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 10,
                        bottom: 0,
                        width: '2px',
                        bgcolor: theme[themeStyle].grey,
                    }}
                />

                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={credits.map(c => c._id)} strategy={verticalListSortingStrategy}>
                        {credits.map((credit, index) => (
                            <SortableCredit
                                key={credit._id}
                                credit={credit}
                                onEdit={handleEditCredit}
                                onDelete={handleDeleteCredit}
                                themeStyle={themeStyle}
                                index={index}
                                isEditing={index === editingIndex}
                                onEditSubmit={handleUpdateCredit}
                                onEditCancel={() => {
                                    setEditingCredit(undefined);
                                    setEditingIndex(null);
                                }}
                                canEdit={canEdit}
                            />
                        ))}
                    </SortableContext>
                </DndContext>
            </Stack>
        </Box>
    );
};

export { CreditsTimeline };
